import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Dialog as MuiDialog, DialogProps as MuiDialogProps, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import useTheme from '~/theme/ThemeProvider';

interface DialogProps {
  open: boolean;
  handleClose: () => void;
  showCloseIcon?: boolean;
  title: string | React.ReactNode;
  body: string | React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: MuiDialogProps['maxWidth'];
}

const DEFAULT_ACTIONS = (handleClose: DialogProps['handleClose']) => (
  <Box>
    <Button onClick={handleClose}>
      <Trans i18nKey="common.actions.close" />
    </Button>
  </Box>
);

export default function Dialog({
  open,
  handleClose,
  title,
  body,
  actions = DEFAULT_ACTIONS(handleClose),
  maxWidth = 'md',
  showCloseIcon = true,
}: DialogProps) {
  const appTheme = useTheme();
  const { t } = useTranslation();

  return (
    <MuiDialog open={open} onClose={handleClose} maxWidth={maxWidth} fullWidth>
      <Stack sx={{ gap: 3, width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography variant="h5">{title}</Typography>
          {showCloseIcon && (
            <IconButton
              onClick={handleClose}
              disableRipple
              sx={{
                color: appTheme.colors.TEXT,
                width: 'auto',
                border: 'none',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              aria-label={t('accessibility.closeDialog')}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Stack sx={{ gap: 2, width: '100%', alignItems: 'normal' }}>{body}</Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            width: '100%',
            gap: 1,
          }}
        >
          {actions}
        </Box>
      </Stack>
    </MuiDialog>
  );
}
